// Help text for: Info tab.
// Keys are referenced as "info.<field>" by <FieldHelp id=... />. Entry shape:
//   title  - field name as shown on screen
//   use    - what the field is for / how to read it
//   source - measured | calculated | backend | default | setting | input
//   how    - where the value comes from: formula, default value, or who sets it
const help = {
  // ===== Details: Consumer Information =====
  consumerId: {
    title: "Consumer ID",
    use: "Your electricity connection's consumer or reference number, kept on your account so you can find it.",
    source: "input",
    how: "You type it here and press Update Details (it is required). It is saved exactly as typed and is not used in any working-out.",
  },
  sanctionedLoad: {
    title: "Sanctioned Load (kW)",
    use: "The most demand your utility connection is approved for.",
    source: "input",
    how: "You type it here in kW and press Update Details (it must be above 0). Used by the MDI Above Sanctioned Load insight, the daily peak-demand use figure (peak demand ÷ sanctioned load) and the Sanctioned Load alert in Notifications.",
  },

  // ===== Details: Unit Prices =====
  gridUnitPrice: {
    title: "Grid Unit Price",
    use: "What you pay per unit (kWh) of grid electricity. It is what turns energy into money across the dashboard.",
    source: "input",
    how: "You type it here in PKR/kWh and press Update Prices. Used for figures like Operating Cost / hr on the Live screen and wasted-energy costs in Insights, which assume PKR 50/kWh while this is blank. Boxes you leave blank keep their old value when you save.",
  },
  gridPeakUnitPrice: {
    title: "Grid Peak Unit Price",
    use: "What you pay per kWh of grid electricity during peak hours.",
    source: "input",
    how: "You type it here in PKR/kWh and press Update Prices. Used to price peak-hour energy, for example in the Energy Efficiency report and initiative savings. Where it is blank, some reports fall back to the normal Grid Unit Price.",
  },
  genUnitPrice: {
    title: "Generator Unit Price",
    use: "What a kWh from your generator costs you, fuel and running cost together.",
    source: "input",
    how: "You type it here in PKR/kWh and press Update Prices. Used to cost the energy your generator supplied, for example on the Historical screen.",
  },
  solarUnitPrice: {
    title: "Solar Unit Price",
    use: "The cost per kWh you choose to put on solar energy.",
    source: "input",
    how: "You type it here in PKR/kWh and press Update Prices. Used to cost the energy solar supplied, for example on the Historical screen.",
  },

  // ===== Details: Password =====
  oldPassword: {
    title: "Old Password",
    use: "Your current password, to prove it is really you making the change.",
    source: "input",
    how: "It is checked when you save, and the change is refused if it does not match.",
  },
  newPassword: {
    title: "New Password",
    use: "The password you want to use from now on.",
    source: "input",
    how: "At least 8 characters, and different from the old one. Passwords run out every 60 days, and you are reminded 7 days before.",
  },
  confirmPassword: {
    title: "Confirm New Password",
    use: "Type the new password again so a typo cannot slip through.",
    source: "input",
    how: "It must match New Password exactly, or the change is not sent.",
  },

  // ===== Details: Phone =====
  countryCode: {
    title: "Country Code",
    use: "Dialling code for your phone number.",
    source: "input",
    how: "Pick +92, +1 or +44; it comes pre-filled with +92. Saved along with the number when you press Update Phone. Premium accounts only.",
  },
  phoneNumber: {
    title: "Phone Number",
    use: "The contact number held on your account.",
    source: "input",
    how: "Digits only, 7 to 15 of them, without the country code. It has to differ from the number already saved. Premium accounts only.",
  },

  // ===== Details: Email =====
  offlineAlertEmail: {
    title: "Offline Meter Alerts: Email Address",
    use: "Where to send an email when your metering hardware stops reporting.",
    source: "input",
    how: "One address per account. The email goes out when every meter on the same gateway has sent nothing for 15 minutes, so one quiet meter on its own will not set it off.",
  },
  branchEmail: {
    title: "Email Notifications by Branch: Email Address",
    use: "Add an address that should get email alerts for one branch, or for the whole site.",
    source: "input",
    how: "Saved under the branch you picked when you press Add Email for Branch. Alert emails, such as rated power exceeded, go to every address under ALL, plus the ones saved under the exact node that raised the alert.",
  },
  branchEmailBranch: {
    title: "Branch",
    use: "Which part of the site the email address is for.",
    source: "input",
    how: "ALL means the whole site. The rest are the branch, grid and main nodes from your hierarchy; solar and generator nodes are not offered.",
  },

  // ===== Details: Branding =====
  companyLogo: {
    title: "Company Logo",
    use: "Your logo, shown in the dashboard header.",
    source: "input",
    how: "PNG or JPG up to 100 KB. It is kept with your account once you save, and the page reloads so the header picks it up. With no logo set, the header falls back to your last saved logo or your initials.",
  },

  // ===== Node Settings: machine card =====
  ratedPower: {
    title: "Rated Power (kW)",
    use: "The machine's nameplate power: how much it is built to draw.",
    source: "setting",
    how: "You enter it here for each node. It drives the Live screen health status (live power ÷ rated power: over 100% Critical, 95% to 100% Warning, otherwise Normal; no status while the machine draws under 0.5 kW or has no rated power), the rated power alert, and the Power and Current insights.",
  },
  loadType: {
    title: "Load Type",
    use: "What this node is, such as Grid, Solar, Motor, Lighting or UPS.",
    source: "setting",
    how: "You pick it here for each node and save the card. It is only a label for the node and is not used in any working-out.",
  },
  color: {
    title: "Color",
    use: "The colour used for this node in charts and diagrams.",
    source: "setting",
    how: "You pick it here for each node. Leave it unset (or black) and the dashboard picks a distinct colour for you.",
  },
  qcpNo: {
    title: "QCP No",
    use: "The Quality Control Point number this temperature sensor belongs to, such as \"QCP 1\".",
    source: "setting",
    how: "You type it here, for temperature sensors only. It labels the sensor in the QCP temperature summary.",
  },
  standardTemp: {
    title: "Standard (°C)",
    use: "The temperature you are aiming for at this control point.",
    source: "setting",
    how: "You type it here, for temperature sensors only. It has to fall in the green band, at or below the lower amber limit, so a room on target never reads Near Limit. It is drawn as the Standard bar in the QCP temperature summary.",
  },
  uclTemp: {
    title: "UCL (°C)",
    use: "Upper Control Limit: the highest temperature you will accept. Above it, the reading is Out of Range.",
    source: "setting",
    how: "Not typed in — it follows the upper amber limit, because UCL and \"UCL Amber\" on a QCP sheet are the same value: the top of the amber band.",
  },
  ualTemp: {
    title: "Upper amber limit (°C)",
    use: "The top of the amber band: amber below it, red above. This is \"UCL Amber\" on a QCP sheet, and it sets UCL too.",
    source: "setting",
    how: "Optional. It must be above both LCL and the lower amber limit. For a −18 °C freezer the sheet uses −12.",
  },
  lalTemp: {
    title: "Lower amber limit (°C)",
    use: "Where green turns amber: in range below it, Near Limit above. This is \"LCL Amber\" on a QCP sheet.",
    source: "setting",
    how: "Optional. It must be above LCL and below the upper amber limit, and at or above the Standard. For a −18 °C freezer the sheet uses −16.",
  },
  lclTemp: {
    title: "LCL (°C)",
    use: "Lower Control Limit: the lowest temperature you will accept. Below it, the reading is Out of Range.",
    source: "setting",
    how: "You type it here, for temperature sensors only. Optional, but it must be below the amber limits. Minus figures are allowed, for freezers and the like.",
  },
  ratedPowerAlert: {
    title: "Alert if rated power exceeded",
    use: "Be told when this machine draws more than its Rated Power.",
    source: "setting",
    how: "Ticked, you get a dashboard notification as soon as a fresh reading at the breaker goes above Rated Power, and it clears once power drops back. An email follows if it stays above for more than 30 minutes. Needs Rated Power.",
  },
  activeOutsideHoursAlert: {
    title: "Alert if active outside hours",
    use: "Be told when this machine runs outside its working hours.",
    source: "setting",
    how: "Ticked, an alert is raised once the machine has drawn more than 0.5 kW outside Working Hours for the number of minutes in a row you set (15 unless you change it). It clears when the machine switches off or working hours begin.",
  },
  inactiveDuringHoursAlert: {
    title: "Alert if inactive during hours",
    use: "Be told when this machine stands idle during its working hours.",
    source: "setting",
    how: "Ticked, an alert is raised once the machine has drawn less than 0.5 kW inside Working Hours for the number of minutes in a row you set (15 unless you change it). It clears when the machine starts or working hours end.",
  },
  workingHours: {
    title: "Working Hours",
    use: "When this machine is meant to be running.",
    source: "setting",
    how: "Start and end hour on the 24-hour clock, whole hours only; the end hour itself counts as outside. Used by the two hours alerts above and by the Machine On In Off Time and Off In On Time insights.",
  },
  alertEmails: {
    title: "Alert Emails",
    use: "Other people who should get this machine's alerts by email.",
    source: "setting",
    how: "Optional. Alerts always show up under the dashboard bell; addresses here get them by email as well. Suggestions come from addresses already on your account. Saved with the card's Save button.",
  },
  lossParams: {
    title: "Loss Calculation Parameters",
    use: "The figures Insights use to put a money value on this machine's problems.",
    source: "setting",
    how: "Loss = Worth of Equipment × Probability of replacement + Production Value × hours affected + Maintenance Cost × Probability of maintenance. Leave a box blank to use its pre-set figure: PKR 5,000,000, PKR 5,000/hr, PKR 5,000, 0.20 and 0.20.",
  },
  worthOfEquipment: {
    title: "Worth of Equipment",
    use: "What it would cost to replace this machine, in PKR.",
    source: "setting",
    how: "Blank means PKR 5,000,000, and it cannot go below PKR 500,000. Multiplied by Probability of replacement to give the Equipment share of each problem's loss.",
  },
  productionValue: {
    title: "Production Value",
    use: "What one hour of this machine's output is worth, in PKR per hour.",
    source: "setting",
    how: "Blank means PKR 5,000/hr, and it cannot go below PKR 1,000/hr. Multiplied by the hours a problem lasted to give the Downtime share of each problem's loss.",
  },
  maintenanceCost: {
    title: "Maintenance Cost",
    use: "What one maintenance visit for this machine usually costs, in PKR.",
    source: "setting",
    how: "Blank means PKR 5,000, and it cannot go below PKR 1,000. Multiplied by Probability of maintenance to give the Maintenance share of each problem's loss.",
  },
  probReplacement: {
    title: "Probability of replacement",
    use: "The chance, from 0 to 1, that a problem damages the machine badly enough to need replacing.",
    source: "setting",
    how: "Blank means 0.20. It has to be between 0 and 1.",
  },
  probMaintenance: {
    title: "Probability of maintenance",
    use: "The chance, from 0 to 1, that a problem leads to a maintenance visit.",
    source: "setting",
    how: "Blank means 0.20. It has to be between 0 and 1.",
  },

  // ===== Notifications =====
  energyReportSchedule: {
    title: "Email Notification",
    use: "Set when the Energy Report email goes out, for each branch or for the whole site.",
    source: "setting",
    how: "For each branch pick who gets it, how often (Daily, Weekly, 15 Days or Monthly) and one or more times of day on the 24-hour clock. Once saved, the reports go out on their own.",
  },
  efficiencyReportSchedule: {
    title: "Energy Efficiency Report Schedule",
    use: "Set when the Energy Efficiency Report email goes out, for each branch or for the whole site.",
    source: "setting",
    how: "The same choices as the Energy Report: who gets it, how often, and the times of day, branch by branch. Once saved, the reports go out on their own.",
  },
  sanctionedLoadAlert: {
    title: "Sanctioned Load",
    use: "Turns the standing check against your sanctioned load on or off.",
    source: "backend",
    how: "The kW figure shown here cannot be edited; it is the one already held on your account. To change it, go to Details > Consumer Information. The switch takes effect the moment you flip it.",
  },
  minimumThreshold: {
    title: "Minimum Threshold",
    use: "A lowest load level (kW) for the standing alert check, plus a switch to turn that check on or off.",
    source: "setting",
    how: "You type the kW here and it saves when you click away from the box. The switch takes effect at once. Both are kept with your account and drive the check.",
  },

  // ===== Multi Tier =====
  mtUsername: {
    title: "Username",
    use: "Login name for a new user who should only see part of your site.",
    source: "input",
    how: "It must not be in use already. The new user is created under your company when you press Save Information.",
  },
  mtPassword: {
    title: "Password",
    use: "The password the new user starts with.",
    source: "input",
    how: "You type it here and it is saved with the new user. They can change it later under Info > Details.",
  },
  primaryNode: {
    title: "Primary Node",
    use: "The part of your hierarchy the new user can see: this node and everything below it.",
    source: "input",
    how: "Pick a node from your hierarchy. You can then untick nodes below it to hide them from this user.",
  },
  recentUsers: {
    title: "Recently Created Users",
    use: "The users created under your company, with their passwords.",
    source: "backend",
    how: "A list of the users held under your company. Click the eye icon to show or hide a password.",
  },

  // ===== Data API =====
  requestUrl: {
    title: "Your request URL",
    use: "A ready-made web address that pulls your own readings out, so you can load them into your own tools.",
    source: "calculated",
    how: "Built from your username, hourly readings, and yesterday to today. Swap YOUR_PASSWORD for your password; mode can be day, hour or minute, and dates are written YYYY-MM-DD, with HH:MM:SS added if you want a time.",
  },
};

export default help;
